/**
 * StudioProgressTracker.js
 *
 * Compact horizontal stepper showing SDLC stage progress.
 * Requirements shows a mini fill-bar based on gathered topics count.
 */
import React from 'react';
import { ALL_TOPICS, countFilled } from '../../services/studioProject.service';

const STAGES = [
  { key: 'requirements', label: 'Requirements', icon: '📋' },
  { key: 'design',       label: 'Design',       icon: '🏛️' },
  { key: 'documentation', label: 'Documentation', icon: '🧾' },
  { key: 'completed',    label: 'Completed',    icon: '✅' },
];

const STAGE_ORDER = ['requirements', 'design', 'documentation', 'completed'];

function stageClass(stageKey, currentPhase) {
  const currentIdx = STAGE_ORDER.indexOf(currentPhase);
  const stageIdx = STAGE_ORDER.indexOf(stageKey);
  if (stageIdx < 0) return 'studio-stage-item locked';
  if (currentIdx < 0) return stageIdx === 0 ? 'studio-stage-item active' : 'studio-stage-item locked';
  if (stageIdx < currentIdx) return 'studio-stage-item done';
  if (stageIdx === currentIdx) return 'studio-stage-item active';
  return 'studio-stage-item locked';
}

function stageIcon(stage, currentPhase) {
  const currentIdx = STAGE_ORDER.indexOf(currentPhase);
  const stageIdx = STAGE_ORDER.indexOf(stage.key);
  if (stageIdx < currentIdx) return '✅';
  if (stageIdx === currentIdx) return stage.icon;
  return '🔒';
}

function StudioProgressTracker({ gathered = {}, currentPhase = 'requirements', onSwitchPhase = null }) {
  const filled    = countFilled(gathered);
  const total     = ALL_TOPICS.length;
  const reqPct    = Math.round((filled / total) * 100);

  return (
    <div className="studio-progress">
      <div className="studio-progress-label">
        <span>SDLC Progress</span>
        <span style={{ color: '#6366f1', fontWeight: 600 }}>{filled}/{total} topics gathered</span>
      </div>

      <div className="studio-progress-stages">
        {STAGES.map((stage, i) => {
          const isReq = stage.key === 'requirements';
          const cls = stageClass(stage.key, currentPhase);
          const canSwitch = Boolean(onSwitchPhase) && !cls.includes('locked');
          const isActive = cls.includes('active');

          return (
            <React.Fragment key={stage.key}>
              <div
                className={cls}
                title={canSwitch ? `Switch to ${stage.label}` : cls.includes('locked') ? 'Finalize previous stage first' : undefined}
                onClick={() => canSwitch && onSwitchPhase(stage.key)}
                style={{ cursor: canSwitch ? 'pointer' : 'default' }}
              >
                <span>{stageIcon(stage, currentPhase)}</span>
                <span>{stage.label}</span>
                {/* Mini bar only for requirements */}
                {isActive && isReq && (
                  <div className="studio-req-bar-track" style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: 4 }}>
                    <div className="studio-req-bar-fill" style={{ width: `${reqPct}%` }} />
                  </div>
                )}
                {isActive && isReq && (
                  <span style={{ fontSize: '0.68rem', color: '#6366f1' }}>{reqPct}%</span>
                )}
              </div>
              {i < STAGES.length - 1 && (
                <span className="studio-stage-connector">›</span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default StudioProgressTracker;
